'use client';

import { createContext, type ReactNode, useContext, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { createStore } from 'zustand/vanilla';
import { DEFAULT_EDITING_MODE, type EditingMode } from '@/app/constants';
import type { StoredLiveClaimSession } from '@/app/types';

export type EditingVideoSource = 'camera' | 'upload' | 'image' | 'screen';
export type EditingRefImageSource = 'upload' | 'built-in';
export type EditingSessionPhase = 'idle' | 'claiming' | 'active' | 'ended';

export type EditingUploadedMaterial = {
  id: string;
  previewUrl: string;
  status: 'uploading' | 'done';
  uri: string | null;
};

export type EditingMaterialSelection = {
  source: EditingRefImageSource | null;
  url: string | null;
  referenceImageUri: string | null;
  editingAssetId: string | null;
  uploadedMaterials?: EditingUploadedMaterial[];
};

interface EditingStoreState {
  /** 当前编辑模式（单路由下由客户端状态驱动；深链 ?mode= 首次挂载时写入）。 */
  editingMode: EditingMode;
  editingVideoSource: EditingVideoSource | null;
  /**
   * 界面上当前选中的输入方式（输入页的 tab 选中态）。
   *
   * 与 editingVideoSource 的区别：后者是「已生效」的来源，刻意以 null 起步，
   * 要等用户按下 CTA 才写入——有值就等于 getUserMedia。而 tab 切换是纯展示态，
   * 不能写进 editingVideoSource，否则点一下 tab 摄像头灯就亮了。
   * 但埋点要的是「用户此刻看到的输入方式」，所以单独存一份。
   */
  editingDisplayedVideoSource: EditingVideoSource;
  editingVideoUrl: string | null;
  editingImageUrl: string | null;
  editingMaterials: Partial<Record<EditingMode, EditingMaterialSelection>>;
  editingSessionId: string | null;
  editingLiveSession: StoredLiveClaimSession | null;
  editingSessionPhase: EditingSessionPhase;
  editingIsClaiming: boolean;
  editingClaimError: string | null;
}

export interface EditingStore extends EditingStoreState {
  setEditingMode: (mode: EditingMode) => void;
  setEditingVideoSource: (source: EditingVideoSource | null) => void;
  setEditingDisplayedVideoSource: (source: EditingVideoSource) => void;
  setEditingVideoUrl: (url: string | null) => void;
  setEditingImageUrl: (url: string | null) => void;
  setEditingMaterialSource: (mode: EditingMode, source: EditingRefImageSource | null) => void;
  setEditingMaterialUrl: (
    mode: EditingMode,
    url: string | null,
    referenceImageUri?: string | null,
    editingAssetId?: string | null,
  ) => void;
  setEditingUploadedMaterials: (mode: EditingMode, materials: EditingUploadedMaterial[]) => void;
  setEditingSessionId: (id: string | null) => void;
  setEditingLiveSession: (session: StoredLiveClaimSession | null) => void;
  setEditingSessionPhase: (phase: EditingSessionPhase) => void;
  setEditingIsClaiming: (isClaiming: boolean) => void;
  setEditingClaimError: (error: string | null) => void;
  clearEditingMaterials: () => void;
  clearEditingLiveSession: () => void;
  resetEditing: () => void;
}

const initialState: EditingStoreState = {
  editingMode: DEFAULT_EDITING_MODE,
  editingVideoSource: null,
  // 输入面板默认选中摄像头，界面态跟着它起步（纯展示，不触发 getUserMedia）。
  editingDisplayedVideoSource: 'camera',
  editingVideoUrl: null,
  editingImageUrl: null,
  editingMaterials: {},
  editingSessionId: null,
  editingLiveSession: null,
  editingSessionPhase: 'idle',
  editingIsClaiming: false,
  editingClaimError: null,
};

export const createEditingStore = (initialEditingMode: EditingMode = DEFAULT_EDITING_MODE) =>
  createStore<EditingStore>()((set) => ({
    ...initialState,
    editingMode: initialEditingMode,
    setEditingMode: (editingMode) => set({ editingMode }),
    // 来源一旦生效，界面态也跟着走：直播中切来源不经过输入页的 tab。
    setEditingVideoSource: (editingVideoSource) =>
      set(
        editingVideoSource
          ? { editingVideoSource, editingDisplayedVideoSource: editingVideoSource }
          : { editingVideoSource },
      ),
    setEditingDisplayedVideoSource: (editingDisplayedVideoSource) => set({ editingDisplayedVideoSource }),
    setEditingVideoUrl: (editingVideoUrl) => set({ editingVideoUrl }),
    setEditingImageUrl: (editingImageUrl) => set({ editingImageUrl }),
    setEditingMaterialSource: (mode, source) =>
      set((state) => ({
        editingMaterials: {
          ...state.editingMaterials,
          [mode]: {
            ...state.editingMaterials[mode],
            source,
            url: state.editingMaterials[mode]?.url ?? null,
            referenceImageUri: state.editingMaterials[mode]?.referenceImageUri ?? null,
            editingAssetId: state.editingMaterials[mode]?.editingAssetId ?? null,
          },
        },
      })),
    setEditingMaterialUrl: (mode, url, referenceImageUri, editingAssetId) =>
      set((state) => ({
        editingMaterials: {
          ...state.editingMaterials,
          [mode]: {
            ...state.editingMaterials[mode],
            source: state.editingMaterials[mode]?.source ?? null,
            url,
            referenceImageUri:
              referenceImageUri === undefined
                ? state.editingMaterials[mode]?.source === 'upload'
                  ? null
                  : url
                : referenceImageUri,
            editingAssetId: editingAssetId ?? null,
          },
        },
      })),
    setEditingUploadedMaterials: (mode, uploadedMaterials) =>
      set((state) => ({
        editingMaterials: {
          ...state.editingMaterials,
          [mode]: {
            ...state.editingMaterials[mode],
            source: state.editingMaterials[mode]?.source ?? null,
            url: state.editingMaterials[mode]?.url ?? null,
            referenceImageUri: state.editingMaterials[mode]?.referenceImageUri ?? null,
            editingAssetId: state.editingMaterials[mode]?.editingAssetId ?? null,
            uploadedMaterials,
          },
        },
      })),
    setEditingSessionId: (editingSessionId) => set({ editingSessionId }),
    setEditingLiveSession: (editingLiveSession) =>
      set({
        editingLiveSession,
        editingSessionId: editingLiveSession?.claim.live.id ?? null,
        editingSessionPhase: editingLiveSession ? 'active' : 'idle',
        editingIsClaiming: false,
        editingClaimError: null,
      }),
    setEditingSessionPhase: (editingSessionPhase) => set({ editingSessionPhase }),
    setEditingIsClaiming: (editingIsClaiming) =>
      set({ editingIsClaiming, ...(editingIsClaiming ? { editingSessionPhase: 'claiming' as const } : {}) }),
    setEditingClaimError: (editingClaimError) =>
      set({ editingClaimError, editingIsClaiming: false, editingSessionPhase: 'idle' }),
    clearEditingMaterials: () => set({ editingMaterials: {} }),
    clearEditingLiveSession: () =>
      set({ editingLiveSession: null, editingSessionId: null, editingIsClaiming: false, editingClaimError: null }),
    resetEditing: () => set(initialState),
  }));

export type EditingStoreApi = ReturnType<typeof createEditingStore>;

const EditingStoreContext = createContext<EditingStoreApi | undefined>(undefined);

export const EditingStoreProvider = ({
  children,
  initialEditingMode,
}: {
  children: ReactNode;
  initialEditingMode?: EditingMode;
}) => {
  const storeRef = useRef<EditingStoreApi | undefined>(undefined);
  if (!storeRef.current) storeRef.current = createEditingStore(initialEditingMode);

  return <EditingStoreContext.Provider value={storeRef.current}>{children}</EditingStoreContext.Provider>;
};

export const useEditingStore = <T,>(
  selector: (state: EditingStore) => T,
  equalityFn: (left: T, right: T) => boolean = shallow,
) => {
  const store = useContext(EditingStoreContext);
  if (!store) throw new Error('useEditingStore must be used within EditingStoreProvider');

  return useStoreWithEqualityFn(store, selector, equalityFn);
};

/**
 * 无 provider 时的兜底 store（模块级单例）。
 *
 * 只为满足 hooks 规则：useEditingStoreOptional 必须无条件调用 useStoreWithEqualityFn，
 * 不能「没 provider 就提前 return」——那会让 hook 调用顺序随渲染变化。
 * 这个 store 只读不写：挂在 provider 外的组件拿到的是初始态，setter 是空操作。
 */
const fallbackEditingStore = createEditingStore();

/**
 * 可选版：供可能挂在 EditingStoreProvider 之外的共享组件（如 EditingSidebar）使用。
 * 无 provider 时读到的是兜底 store 的初始态，写入不会影响任何真实工作区。
 */
export const useEditingStoreOptional = <T,>(selector: (state: EditingStore) => T) => {
  const store = useContext(EditingStoreContext);
  return useStoreWithEqualityFn(store ?? fallbackEditingStore, selector, shallow);
};
